/**
 * @file src/input/midi/takeoverModes.js
 * @description MIDI takeover: what happens the FIRST time a physical control is moved after it
 * started addressing a different parameter than the one it last matched.
 *
 * A fader sends an absolute value. The moment it is pointed at a new destination — another orbiter
 * loads, a saved mapping is opened, a dimension switches under it — the control's physical position
 * no longer means what the parameter currently reads, so the first touch makes the value jump.
 * The mode decides what that first touch does:
 *
 *   none          — the value is written straight through. It can jump. (Today's behaviour.)
 *   pick-up       — writes are SUPPRESSED until the control passes the parameter's value; from then
 *                   on it tracks 1:1. Smooth, but the pick-up point is invisible.
 *   value-scaling — the parameter is moved toward the control proportionally, so the two converge
 *                   at the end of the control's travel; once they meet it tracks 1:1.
 *
 * Everything here is in MIDI-normalized space `[0,1]` — the same space `midiScale.js` maps to and
 * from — so a logarithmic parameter converges by knob POSITION, exactly like a linear one. The
 * module is pure: no storage, no DOM, no clock. The caller owns one state object per
 * (orbiter, parameter, control) and hands it back on every message.
 */

/** Write straight through; the value can jump. */
export const TAKEOVER_NONE = 'none';
/** Suppress writes until the control passes the parameter's value. */
export const TAKEOVER_PICKUP = 'pick-up';
/** Converge the parameter onto the control as the control is moved. */
export const TAKEOVER_SCALING = 'value-scaling';

/** Every mode, in the order the dropdown offers them. */
export const TAKEOVER_MODES = [TAKEOVER_NONE, TAKEOVER_PICKUP, TAKEOVER_SCALING];

/** Nothing changes for anyone who does not choose a mode. */
export const DEFAULT_TAKEOVER_MODE = TAKEOVER_NONE;

/**
 * Half a CC step (1/127). Two values this close are the same fader position as far as a 7-bit
 * controller can express, so treating them as equal is what makes pick-up reachable at all —
 * an exact-equality test would only ever engage by luck.
 */
const EPSILON = 0.5 / 127;

/** @param {*} value @returns {boolean} whether it is a mode this module implements. */
export function isTakeoverMode(value) {
  return TAKEOVER_MODES.includes(value);
}

/** @param {*} value @returns {string} the value if it is a real mode, else the default. */
export function coerceTakeoverMode(value) {
  return isTakeoverMode(value) ? value : DEFAULT_TAKEOVER_MODE;
}

/**
 * A fresh state object for one (orbiter, parameter, control) triple.
 *
 * `engaged` latches: once the control and the parameter have met, the mode is out of the way until
 * something resets the state (the orbiter loads, the binding is re-learned). `lastControl` is the
 * previous control position, which is what gives movement a DIRECTION — the first message after a
 * reset has none, which is why it cannot take over.
 * @returns {{ engaged: boolean, lastControl: number|null }}
 */
export function createTakeoverState() {
  return { engaged: false, lastControl: null };
}

function clamp01(value) {
  return Math.min(1, Math.max(0, value));
}

/**
 * Decide what a control movement should write.
 *
 * @param {string} mode one of {@link TAKEOVER_MODES}; anything else behaves as `none`.
 * @param {{ engaged: boolean, lastControl: number|null }} state MUTATED in place — the caller keeps
 *   one per (orbiter, parameter, control) and passes the same object every message.
 * @param {number} control the incoming control position, MIDI-normalized `[0,1]`.
 * @param {number|null} current the parameter's present value in the SAME normalized space, or null
 *   when it cannot be read (an unregistered param, a control with no range) — with nothing to
 *   converge onto there is no takeover to do, so the write goes through and the state engages.
 * @returns {number|null} the value to write (normalized), or null to write NOTHING this message.
 */
export function applyTakeover(mode, state, control, current) {
  if (!state || !Number.isFinite(control)) {
    return Number.isFinite(control) ? control : null;
  }
  const incoming = clamp01(control);

  if (mode !== TAKEOVER_PICKUP && mode !== TAKEOVER_SCALING) {
    // `none` — and any unknown mode, which must never be more restrictive than the default.
    state.lastControl = incoming;
    return incoming;
  }

  if (state.engaged) {
    state.lastControl = incoming;
    return incoming;
  }

  if (!Number.isFinite(current)) {
    state.engaged = true;
    state.lastControl = incoming;
    return incoming;
  }
  const target = clamp01(current);

  // Already on the parameter (within a CC step): there is nothing to take over.
  if (Math.abs(incoming - target) <= EPSILON) {
    state.engaged = true;
    state.lastControl = incoming;
    return incoming;
  }

  const previous = state.lastControl;
  state.lastControl = incoming;

  // The first message after a reset establishes where the control IS. With no previous position
  // there is no direction of travel, so neither mode can act on it yet.
  if (previous === null) {
    return null;
  }

  const delta = incoming - previous;
  if (delta === 0) {
    return null;
  }

  // The control passed THROUGH the parameter's value: that is the pick-up, in both modes.
  if ((previous < target && incoming > target) || (previous > target && incoming < target)) {
    state.engaged = true;
    return incoming;
  }

  if (mode === TAKEOVER_PICKUP) {
    return null;
  }

  // Value scaling. The parameter consumes the same FRACTION of its remaining travel that the
  // control just consumed of its own, in the direction of movement — so the two are guaranteed to
  // arrive together at the end of the control's throw, whichever side of the parameter the control
  // started on. (Control above the parameter and rising: the parameter has more headroom, so it
  // moves further and catches up. Control below and rising: less headroom, so it moves less and is
  // caught. Both meet at 1.)
  const controlTravel = delta > 0 ? 1 - previous : previous;
  if (controlTravel <= EPSILON) {
    // The control is already parked at the end it is moving toward; there is no room left to scale
    // through. Snap rather than divide by nothing.
    state.engaged = true;
    return incoming;
  }
  const parameterTravel = delta > 0 ? 1 - target : target;
  const next = clamp01(target + delta * (parameterTravel / controlTravel));

  if (Math.abs(next - incoming) <= EPSILON) {
    state.engaged = true;
    return incoming;
  }
  return next;
}
